import { Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { SearchCviIcon } from '../icons-library/search-cvi';
import { CrossIcon } from '../icons-library/cross';

type SearchFieldProps = {
    value: string;
    onInput: (value: string) => void;
    onSubmit: (value: string) => void;
    /** Extra work when the clear button empties the field, beyond `onInput('')`. */
    onClear?: () => void;
    placeholder?: string;
    ariaLabel?: string;
    id?: string;
    autofocus?: boolean;
    inputRef?: (el: HTMLInputElement) => void;
    clearButtonRef?: (el: HTMLButtonElement) => void;
    class?: string;
    onKeyDown?: (event: KeyboardEvent) => void;
    /** Wire the input up as a combobox driving a suggestion listbox rendered by the caller. */
    combobox?: {
        listId: string;
        expanded: boolean;
        activeOptionId?: string;
    };
};

/* DS FormKit search field (Figma `.Search field – base`, 183:19496): a search glyph leading
   the text, and a ghost clear button trailing it once there is something to clear. The field
   shell follows `fieldShellClasses` except for the border, which the DS draws in `border/default`
   here rather than `border/strong`. The native cancel button is hidden so only ours shows. */
export const SearchField = (props: SearchFieldProps) => {
    const { localize } = useContext(AppContext);

    const clear = () => {
        props.onInput('');
        props.onClear?.();
    };

    return (
        <form
            role="search"
            class={clsx('relative w-full', props.class)}
            novalidate
            onSubmit={(event) => {
                event.preventDefault();
                props.onSubmit(props.value);
            }}
        >
            <SearchCviIcon
                size="default"
                ariaHidden
                class="pointer-events-none absolute left-cvi-field-x top-1/2 -translate-y-1/2 text-cvi-fg-default"
            />
            {/* Callback form on both refs: a bare `ref={props.x}` compiles to `props.x = el` when
                no function is passed, and props forwarded by a parent are getter-only. */}
            <input
                ref={(el) => props.inputRef?.(el)}
                id={props.id}
                type="search"
                value={props.value}
                placeholder={props.placeholder}
                aria-label={props.ariaLabel ?? localize('search', 'Search')}
                autocomplete="off"
                autofocus={props.autofocus}
                enterkeyhint="search"
                role={props.combobox ? 'combobox' : undefined}
                aria-autocomplete={props.combobox ? 'list' : undefined}
                aria-expanded={props.combobox ? props.combobox.expanded : undefined}
                aria-controls={props.combobox?.listId}
                aria-activedescendant={props.combobox?.activeOptionId}
                onKeyDown={props.onKeyDown}
                onInput={(event) => props.onInput(event.currentTarget.value)}
                class={clsx(
                    'w-full rounded-cvi-md border border-cvi-border-default bg-cvi-bg-field py-cvi-field-y pl-cvi-search-field-l pr-cvi-search-field-r',
                    'label-md font-cvi-regular text-cvi-fg-default placeholder:text-cvi-fg-muted',
                    'focus:outline-none focus:border-cvi-border-interactive focus:ring-4 focus:ring-cvi-focus-default focus:ring-offset-2 focus:ring-offset-cvi-bg-body',
                    '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
                )}
            />
            <Show when={props.value}>
                <button
                    ref={(el) => props.clearButtonRef?.(el)}
                    type="button"
                    class="icon-button-ghost icon-button-ghost-sm absolute right-cvi-search-clear top-1/2 -translate-y-1/2"
                    onClick={clear}
                    aria-label={localize('clear-search', 'Clear search')}
                >
                    <CrossIcon size="small" ariaHidden />
                </button>
            </Show>
        </form>
    );
};
